import React from "react";
import { styled } from "@mui/material/styles";
import {
    Typography,
    Box,
    Container,
    Grid,
    Chip,
    Button,
    Card,
    CardContent,
} from "@mui/material";
import { Fade } from "react-awesome-reveal";
import { useNavigate } from "react-router-dom";

// ── MUI Icons ──────────────────────────────────────────────────
import RocketLaunchIcon      from "@mui/icons-material/RocketLaunch";
import EmojiEventsIcon       from "@mui/icons-material/EmojiEvents";
import PeopleAltIcon         from "@mui/icons-material/PeopleAlt";
import WorkspacePremiumIcon  from "@mui/icons-material/WorkspacePremium";
import PublicIcon            from "@mui/icons-material/Public";
import AutoAwesomeIcon       from "@mui/icons-material/AutoAwesome";
import SchoolIcon            from "@mui/icons-material/School";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import VerifiedIcon          from "@mui/icons-material/Verified";
import HandshakeIcon         from "@mui/icons-material/Handshake";
import TrendingUpIcon        from "@mui/icons-material/TrendingUp";
import CodeIcon              from "@mui/icons-material/Code";
import BuildIcon             from "@mui/icons-material/Build";
import SecurityIcon          from "@mui/icons-material/Security";
import ArchitectureIcon      from "@mui/icons-material/Architecture";
import DevicesIcon           from "@mui/icons-material/Devices";
import BusinessCenterIcon    from "@mui/icons-material/BusinessCenter";
import StarIcon              from "@mui/icons-material/Star";
import PsychologyIcon        from "@mui/icons-material/Psychology";

// ============================================================
//  BRAND CONSTANTS
//  Single source of truth for all colours on this page.
//  Change a value here and it updates everywhere automatically.
// ============================================================
const BRAND = {
    dark:      "#100720",                  // Deep purple-black — primary background
    gold:      "#E5C95F",                  // Goldenrod — primary accent
    goldenrod: "#DAA520",                  // Pure goldenrod — secondary accent / hover Altered
    white:     "#FFFFFF",                  // Pure white — headings
    dimWhite:  "rgba(255,255,255,0.62)",   // Muted white — body text
    cardBg:    "rgba(255,255,255,0.04)",   // Semi-transparent card background
    border:    "rgba(229,201,95,0.2)",     // Gold-tinted border
    orange:    "#E5C95F",                  // Accent for pillar 1 (passion)
    green:     "#DAA520",                  // Accent for pillar 3 (consultancy) Altered
};

// ============================================================
//  STYLED COMPONENTS
//  All reusable visual building blocks live here.
//  Pattern: define styled component → comment its purpose → use in JSX.
// ============================================================

/**
 * PageWrapper
 * Root container for the entire Mission page.
 * Dark background + hidden x-overflow to contain animated elements.
 */
const PageWrapper = styled(Box)({
    width: "100%",
    minHeight: "100vh",
    backgroundColor: BRAND.dark,
    fontFamily: "'Poppins', sans-serif",
    overflowX: "hidden",
});

/**
 * HeroSection
 * Full-viewport hero with a marquee background text effect.
 * The ::before layer renders the giant faded "MISSION" text.
 */
const HeroSection = styled(Box)({
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    padding: "0 0 64px",
    overflow: "hidden",
    // Dark radial vignette so bottom content stays readable
    background:
        `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(229,201,95,0.1) 0%, transparent 60%), ${BRAND.dark}`,
});

/**
 * MarqueeText
 * Giant decorative background text that slides left continuously.
 * animation-play-state: running means it loops forever.
 * pointer-events: none so it doesn't block click targets.
 */
const MarqueeText = styled(Typography)({
    position: "absolute",
    top: "50%",
    left: 0,
    transform: "translateY(-50%)",
    whiteSpace: "nowrap",
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 800,
    color: "rgba(229,201,95,0.04)",  // Barely visible — just texture
    letterSpacing: "-0.02em",
    fontSize: "clamp(5rem, 12vw, 10rem)",
    animation: "marquee 22s linear infinite",
    pointerEvents: "none",
    userSelect: "none",
    // Define @keyframes inline using MUI's sx-compatible string form
    "@keyframes marquee": {
        from: { transform: "translateY(-50%) translateX(0)" },
        to:   { transform: "translateY(-50%) translateX(-50%)" },
    },
});

/**
 * GoldChip
 * Reusable pill-shaped label badge.
 * Used as eyebrow labels above section headings.
 */
const GoldChip = styled(Chip)({
    backgroundColor: "rgba(229,201,95,0.1)",
    border: `1px solid ${BRAND.border}`,
    color: BRAND.gold,
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 600,
    fontSize: "0.68rem",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    "& .MuiChip-icon": { color: BRAND.gold },
});

/**
 * SectionWrapper
 * Alternating section backgrounds for page rhythm.
 * variant="alt" → very slightly lighter than the base dark bg.
 */
const SectionWrapper = styled(Box)(({ variant }) => ({
    width: "100%",
    padding: "80px 0",
    backgroundColor:
        variant === "alt" ? "rgba(255,255,255,0.025)" : BRAND.dark,
    borderTop: `1px solid ${BRAND.border}`,
}));

/**
 * GoldDivider
 * Short decorative gold line under section headings.
 * Gives a premium branded feel consistent across both pages.
 */
const GoldDivider = styled(Box)({
    width: "56px",
    height: "3px",
    background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
    borderRadius: "2px",
    margin: "14px auto 0",
});

/**
 * FeatureCard
 * Dark glass-morphism card with gold glow on hover.
 * height: "100%" ensures consistent height in Grid rows.
 */
const FeatureCard = styled(Card)({
    backgroundColor: BRAND.cardBg,
    border: `1px solid ${BRAND.border}`,
    borderRadius: "16px",
    backdropFilter: "blur(10px)",
    transition: "transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
    cursor: "default",
    height: "100%",
    "&:hover": {
        transform: "translateY(-5px)",
        borderColor: BRAND.gold,
        boxShadow: `0 16px 36px rgba(229,201,95,0.1)`,
    },
});

/**
 * GoldButton
 * Primary CTA button — gold gradient, dark text.
 * Lifts and glows on hover.
 */
const GoldButton = styled(Button)({
    background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
    color: BRAND.dark,
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 700,
    fontSize: "0.85rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "12px 32px",
    borderRadius: "8px",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    "&:hover": {
        background: `linear-gradient(135deg, ${BRAND.goldenrod}, ${BRAND.gold})`,
        transform: "translateY(-2px)",
        boxShadow: `0 10px 28px rgba(229,201,95,0.35)`,
    },
});

/**
 * IconCircle
 * Circular icon container with gold-tinted ring.
 * flexShrink: 0 prevents squishing inside flex parents.
 */
const IconCircle = styled(Box)({
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    backgroundColor: "rgba(229,201,95,0.1)",
    border: `1px solid ${BRAND.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: BRAND.gold,
});

/**
 * PillarCard
 * Dark card variant for the Three Pillars section.
 * accent prop controls the top border highlight colour.
 *
 * Usage: <PillarCard accent="#FF6B35"> ... </PillarCard>
 */
const PillarCard = styled(Box)(({ accent }) => ({
    backgroundColor: BRAND.cardBg,
    border: `1px solid ${BRAND.border}`,
    borderRadius: "16px",
    padding: "36px 28px",
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    height: "100%",
    // Top border highlight using the accent colour
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: 0, right: 0,
        height: "3px",
        background: accent || BRAND.gold,
    },
    "&:hover": {
        transform: "translateY(-5px)",
        boxShadow: `0 20px 40px rgba(229,201,95,0.08)`,
    },
}));

/**
 * ValueRow
 * Horizontal row for each core value — editorial list style.
 * Subtle background shift on hover indicates interactivity.
 */
const ValueRow = styled(Box)({
    display: "grid",
    gridTemplateColumns: "60px 1fr 2fr 36px",
    gap: "20px",
    alignItems: "center",
    padding: "20px 0",
    borderBottom: `1px solid ${BRAND.border}`,
    transition: "background 0.2s ease",
    borderRadius: "8px",
    "&:hover": { backgroundColor: "rgba(229,201,95,0.025)" },
    // Remove border on the last row (no double-border with section below)
    "&:last-child": { borderBottom: "none" },
});

// ============================================================
//  DATA CONSTANTS
//  All copy and content live here — separate from JSX/rendering.
//  Junior dev tip: never hardcode strings inside your components.
//  Keep data here → iterate with .map() in the JSX below.
// ============================================================

/**
 * THREE_PILLARS
 * The three core pillars of the platform's mission.
 * accent = top-border colour unique to each pillar.
 */
const THREE_PILLARS = [
    {
        icon: <StarIcon fontSize="large" />,
        accent: BRAND.orange,
        roman: "I",
        title: "Reignite the Passion",
        desc: "AI hasn't made developers obsolete — it has made passionate, skilled developers more valuable. We inspire people to fall in love with coding through gamification, rewards, and real outcomes.",
    },
    {
        icon: <WorkspacePremiumIcon fontSize="large" />,
        accent: BRAND.gold,
        roman: "II",
        title: "Train & Certify",
        desc: "We provide structured, gamified learning tracks in Frontend, Backend, Fullstack, IoT, Java, and Python — each ending with a certification exam and a downloadable certificate of competence.",
    },
    {
        icon: <BusinessCenterIcon fontSize="large" />,
        accent: BRAND.green,
        roman: "III",
        title: "Enable Careers & Consult",
        desc: "Beyond training, we bridge the gap between learning and working. We offer software consultancy services and connect certified graduates with real opportunities in the industry.",
    },
];

/**
 * CORE_VALUES
 * Six values that define how the platform operates.
 * emoji is a decorative icon on the far right of each row.
 */
const CORE_VALUES = [
    { num: "01", icon: <AutoAwesomeIcon />, name: "Make It Fun",         desc: "Learning should feel like play. We use gamification to keep motivation high every session.",               },
    { num: "02", icon: <EmojiEventsIcon />, name: "Reward Excellence",   desc: "Stars, golden buzzers, and certificates are real recognition of genuine achievement.",                   },
    { num: "03", icon: <PublicIcon />,       name: "Be Accessible",       desc: "Anyone with a screen and curiosity can become a developer. We remove barriers, not build them.",          },
    { num: "04", icon: <BuildIcon />,        name: "Build Real Skills",   desc: "Our curriculum is designed around what employers actually need — not outdated theory.",                    },
    { num: "05", icon: <PsychologyIcon />,   name: "Embrace AI",          desc: "We teach learners to work with AI, not fear it. AI is a tool — developers are the craftsmen.",            },
    { num: "06", icon: <VerifiedIcon />,     name: "Certify Competence",  desc: "Our certificates mean something. You earn them by demonstrating real knowledge, not just showing up.",   },
];

/**
 * COMMITMENTS
 * Six commitments the platform makes to its learners.
 * Each has a MUI icon, a title, and a one-sentence description.
 */
const COMMITMENTS = [
    { icon: <LightbulbIcon />,         title: "Inspire Curiosity",              desc: "We craft lessons that spark genuine interest — making the complex feel exciting." },
    { icon: <TrendingUpIcon />,        title: "Deliver Real Outcomes",          desc: "Our measure of success is certified developers entering the workforce." },
    { icon: <CodeIcon />,              title: "Stay Current",                   desc: "Curriculum evolves with technology, reflecting real industry trends and tools." },
    { icon: <SchoolIcon />,            title: "Support Every Learner",          desc: "From beginner to advanced — we meet learners where they are." },
    { icon: <PeopleAltIcon />,         title: "Build Community",                desc: "Developers grow faster together — we foster learners, mentors, and professionals." },
    { icon: <BusinessCenterIcon />,    title: "Excel in Consultancy",           desc: "Our consultancy delivers the same excellence that drives our training programs." },
];

/**
 * CONSULT_SERVICES
 * Six software consultancy service tags shown in the consultancy section.
 */
const CONSULT_SERVICES = [
    { icon: <ArchitectureIcon />, label: "System Architecture" },
    { icon: <SecurityIcon />,     label: "Security Review"     },
    { icon: <RocketLaunchIcon />, label: "MVP Development"     },
    { icon: <DevicesIcon />,      label: "Digital Strategy"    },
    { icon: <HandshakeIcon />,    label: "Staff Augmentation"  },
    { icon: <BuildIcon />,        label: "Tech Audit"          },
];

// ============================================================
//  MAIN COMPONENT — Mission
// ============================================================

/**
 * Mission
 * Full-page Mission component for the platform.
 *
 * Page sections (in order):
 *  1. Hero              — editorial dark hero with marquee background
 *  2. Mission Statement — one-paragraph bold statement
 *  3. Three Pillars     — pillar cards with coloured top borders
 *  4. Core Values       — editorial table-style value rows
 *  5. Commitments       — six commitment cards
 *  6. Promise Banner    — full-width gold CTA strip
 *  7. Consultancy       — service tags + CTA card → /signup
 *  8. Footer strip      — single-line brand statement
 *
 * Add this to App.js:
 *   import Mission from "./pages/Mission";
 *   <Route path="/mission" element={<Mission />} />
 */
export default function Mission() {
    // useNavigate — SPA routing to /signup without page reload
    const navigate = useNavigate();

    return (
        <PageWrapper>

            {/* ════════════════════════════════════════════════════
          1. HERO SECTION
          Editorial dark hero — bottom-anchored text layout.
          Marquee background adds depth without distracting.
      ════════════════════════════════════════════════════ */}
            <HeroSection>
                {/* Repeating marquee — "MISSION • CODE • BUILD •" string doubled
            so the animation loops seamlessly without a visible jump */}
                <MarqueeText>
                    MISSION&nbsp;•&nbsp;CODE&nbsp;•&nbsp;BUILD&nbsp;•&nbsp;CERTIFY&nbsp;•&nbsp;INSPIRE&nbsp;•&nbsp;
                    MISSION&nbsp;•&nbsp;CODE&nbsp;•&nbsp;BUILD&nbsp;•&nbsp;CERTIFY&nbsp;•&nbsp;INSPIRE&nbsp;•&nbsp;
                    MISSION&nbsp;•&nbsp;CODE&nbsp;•&nbsp;BUILD&nbsp;•&nbsp;CERTIFY&nbsp;•&nbsp;INSPIRE&nbsp;•&nbsp;
                </MarqueeText>

                {/* Hero content — anchored to the bottom */}
                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>

                    {/* Eyebrow badge */}
                    <Fade direction="down" triggerOnce>
                        <Box mb={3}>
                            <GoldChip
                                icon={<RocketLaunchIcon sx={{ fontSize: 16 }} />}
                                label="Our Mission"
                            />
                        </Box>
                    </Fade>

                    {/* Main headline — "Earn." in gold for brand contrast */}
                    <Fade direction="up" triggerOnce delay={200}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 800,
                                fontSize: { xs: "3.5rem", sm: "5rem", md: "7.5rem" },
                                lineHeight: 0.92,
                                letterSpacing: "-0.04em",
                                color: BRAND.white,
                                mb: 4,
                            }}
                        >
                            Code.<br />
                            <Box
                                component="span"
                                sx={{
                                    // Gold gradient text — same technique as Vision.jsx
                                    background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                Earn.
                            </Box>
                            <br />
                            Build.
                        </Typography>
                    </Fade>

                    {/* Hero bottom — tagline left, stat right */}
                    <Fade direction="up" triggerOnce delay={400}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-end",
                                flexWrap: "wrap",
                                gap: 3,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: { xs: "0.95rem", md: "1.1rem" },
                                    fontWeight: 300,
                                    fontStyle: "italic",
                                    color: BRAND.dimWhite,
                                    maxWidth: 480,
                                    lineHeight: 1.75,
                                }}
                            >
                                "We exist to make software development exciting, rewarding, and career-defining
                                for every person who dares to learn — in the age of AI and beyond."
                            </Typography>

                            {/* Hero stat — quick brand credibility signal */}
                            <Box textAlign={{ xs: "left", md: "right" }}>
                                <Typography
                                    sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        fontWeight: 800,
                                        fontSize: "3rem",
                                        color: BRAND.gold,
                                        lineHeight: 1,
                                    }}
                                >
                                    5+
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.72rem", color: BRAND.dimWhite, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                                    Developer Tracks
                                </Typography>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </HeroSection>

            {/* ════════════════════════════════════════════════════
          2. MISSION STATEMENT
          Single bold paragraph — the platform's core mission
          in plain language with gold/green inline highlights.
      ════════════════════════════════════════════════════ */}
            <SectionWrapper>
                <Container maxWidth="lg">
                    <Fade direction="up" triggerOnce>
                        <GoldChip label="What We Are Here to Do" sx={{ mb: 3 }} />
                        <Typography
                            sx={{
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 700,
                                fontSize: { xs: "1.4rem", md: "2rem" },
                                lineHeight: 1.4,
                                color: BRAND.white,
                                maxWidth: 900,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Our mission is to{" "}
                            <Box component="span" sx={{ color: BRAND.orange }}>reignite the passion for software development</Box>,
                            train a new generation of{" "}
                            <Box component="span" sx={{ color: BRAND.gold }}>certified, job-ready developers</Box>,
                            and prove that in the age of AI,{" "}
                            <Box component="span" sx={{ color: BRAND.green }}>human creativity and technical skill</Box>{" "}
                            matter more than ever.
                        </Typography>
                    </Fade>
                </Container>
            </SectionWrapper>

            {/* ════════════════════════════════════════════════════
          3. THREE PILLARS OF THE MISSION
          Three PillarCards — each with a unique accent colour
          on the top border to visually differentiate the pillars.
      ════════════════════════════════════════════════════ */}
            <SectionWrapper variant="alt">
                <Container maxWidth="lg">
                    <Fade direction="up" triggerOnce>
                        <Box textAlign="center" mb={6}>
                            <GoldChip label="Three Pillars of Our Mission" sx={{ mb: 2 }} />
                            <Typography
                                variant="h2"
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 800,
                                    fontSize: { xs: "2rem", md: "2.8rem" },
                                    color: BRAND.white,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                What We Stand On
                            </Typography>
                            <GoldDivider />
                        </Box>
                    </Fade>

                    <Grid container spacing={3}>
                        {THREE_PILLARS.map((pillar, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Fade direction="up" triggerOnce delay={i * 130}>
                                    {/* PillarCard — accent prop sets the coloured top border */}
                                    <PillarCard accent={pillar.accent}>
                                        {/* Decorative Roman numeral — barely visible background text */}
                                        <Typography
                                            sx={{
                                                position: "absolute",
                                                top: "16px", right: "20px",
                                                fontFamily: "'Poppins', sans-serif",
                                                fontWeight: 800,
                                                fontSize: "3.5rem",
                                                color: "rgba(255,255,255,0.04)",
                                                lineHeight: 1,
                                                userSelect: "none",
                                            }}
                                        >
                                            {pillar.roman}
                                        </Typography>

                                        {/* Icon in a gold circle */}
                                        <IconCircle sx={{ mb: 2 }}>{pillar.icon}</IconCircle>

                                        <Typography
                                            sx={{
                                                fontFamily: "'Poppins', sans-serif",
                                                fontWeight: 700,
                                                fontSize: "1.25rem",
                                                color: BRAND.white,
                                                mb: 1.5,
                                            }}
                                        >
                                            {pillar.title}
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", color: BRAND.dimWhite, lineHeight: 1.7 }}>
                                            {pillar.desc}
                                        </Typography>
                                    </PillarCard>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </SectionWrapper>

            {/* ════════════════════════════════════════════════════
          4. CORE VALUES
          Editorial table-style layout — number / name / desc / emoji.
          ValueRow uses CSS Grid (not MUI Grid) for tight column control.
      ════════════════════════════════════════════════════ */}
            <SectionWrapper>
                <Container maxWidth="lg">
                    <Fade direction="up" triggerOnce>
                        <Box mb={5}>
                            <GoldChip label="What We Stand For" sx={{ mb: 2 }} />
                            <Typography
                                variant="h2"
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 800,
                                    fontSize: { xs: "2rem", md: "2.8rem" },
                                    color: BRAND.white,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                Our Core Values
                            </Typography>
                            <GoldDivider sx={{ mx: 0, mt: 1.5 }} />
                        </Box>
                    </Fade>

                    {/* Value rows — rendered from CORE_VALUES array */}
                    {CORE_VALUES.map((v, i) => (
                        <Fade direction="up" triggerOnce delay={i * 80} key={i}>
                            <ValueRow>
                                {/* Column 1: index number */}
                                <Typography
                                    sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        fontWeight: 700,
                                        fontSize: "0.8rem",
                                        color: BRAND.dimWhite,
                                        letterSpacing: "0.1em",
                                    }}
                                >
                                    {v.num}
                                </Typography>

                                {/* Column 2: value name with icon */}
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{ color: BRAND.gold, display: "flex" }}>{v.icon}</Box>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1rem", color: BRAND.white }}>
                                        {v.name}
                                    </Typography>
                                </Box>

                                {/* Column 3: description */}
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.875rem", color: BRAND.dimWhite, lineHeight: 1.6, display: { xs: "none", md: "block" } }}>
                                    {v.desc}
                                </Typography>

                                {/* Column 4: decorative emoji */}
                                <Typography sx={{ fontSize: "1.4rem", display: { xs: "none", sm: "block" } }}>{v.emoji}</Typography>
                            </ValueRow>
                        </Fade>
                    ))}
                </Container>
            </SectionWrapper>

            {/* ════════════════════════════════════════════════════
          5. COMMITMENTS TO LEARNERS
          Six FeatureCards — what the platform promises to do.
      ════════════════════════════════════════════════════ */}
            <SectionWrapper variant="alt">
                <Container maxWidth="lg">
                    <Fade direction="up" triggerOnce>
                        <Box textAlign="center" mb={6}>
                            <GoldChip label="How We Show Up" sx={{ mb: 2 }} />
                            <Typography
                                variant="h2"
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 800,
                                    fontSize: { xs: "2rem", md: "2.8rem" },
                                    color: BRAND.white,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                Our Commitments to Learners
                            </Typography>
                            <GoldDivider />
                        </Box>
                    </Fade>

                    <Grid container spacing={3}>
                        {COMMITMENTS.map((c, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Fade direction="up" triggerOnce delay={i * 100}>
                                    <FeatureCard>
                                        <CardContent sx={{ p: 3 }}>
                                            <IconCircle sx={{ mb: 2 }}>{c.icon}</IconCircle>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1rem", color: BRAND.white, mb: 1 }}>
                                                {c.title}
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.875rem", color: BRAND.dimWhite, lineHeight: 1.65 }}>
                                                {c.desc}
                                            </Typography>
                                        </CardContent>
                                    </FeatureCard>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </SectionWrapper>

            {/* ════════════════════════════════════════════════════
          6. PROMISE BANNER
          Full-width gold-gradient strip — our promise statement.
          Links to Vision page for more context.
      ════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${BRAND.goldenrod}, ${BRAND.gold})`,
                    py: { xs: 7, md: 10 },
                    px: 3,
                    textAlign: "center",
                }}
            >
                <Fade direction="up" triggerOnce>
                    <GoldChip label="Our Promise" sx={{ mb: 2, backgroundColor: "rgba(16,7,32,0.2)", color: BRAND.dark, border: "1px solid rgba(16,7,32,0.2)" }} />
                    <Typography
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 800,
                            fontSize: { xs: "1.8rem", md: "3rem" },
                            color: BRAND.dark,
                            maxWidth: 700,
                            mx: "auto",
                            mb: 2,
                            lineHeight: 1.1,
                            letterSpacing: "-0.03em",
                        }}
                    >
                        Every learner deserves to become a builder.
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "1rem",
                            color: "rgba(16,7,32,0.7)",
                            maxWidth: 500,
                            mx: "auto",
                            mb: 4,
                            lineHeight: 1.7,
                        }}
                    >
                        We promise to make your journey as rewarding as the destination —
                        with every star, every buzzer, and every certificate earned.
                    </Typography>

                    {/* Outlined dark button → Vision page */}
                    <Button
                        variant="outlined"
                        onClick={() => navigate("/vision")}
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: BRAND.dark,
                            borderColor: BRAND.dark,
                            px: 4, py: 1.5,
                            borderRadius: "8px",
                            "&:hover": { backgroundColor: "rgba(16,7,32,0.08)", borderColor: BRAND.dark },
                        }}
                    >
                        See Our Vision →
                    </Button>
                </Fade>
            </Box>

            {/* ════════════════════════════════════════════════════
          7. SOFTWARE CONSULTANCY SECTION
          Left: mission quote + six service tags.
          Right: CTA card with GoldButton → /signup.
      ════════════════════════════════════════════════════ */}
            <SectionWrapper variant="alt">
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="flex-start">

                        {/* Left column — consultancy mission */}
                        <Grid item xs={12} md={7}>
                            <Fade direction="left" triggerOnce>
                                <GoldChip label="Beyond Training" sx={{ mb: 2 }} />
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        fontWeight: 800,
                                        fontSize: { xs: "2rem", md: "2.5rem" },
                                        color: BRAND.white,
                                        letterSpacing: "-0.02em",
                                        mb: 1.5,
                                    }}
                                >
                                    Software Consultancy Mission
                                </Typography>

                                {/* Highlighted quote with left gold border */}
                                <Box
                                    sx={{
                                        p: "20px 24px",
                                        borderLeft: `4px solid ${BRAND.gold}`,
                                        backgroundColor: "rgba(229,201,95,0.04)",
                                        borderRadius: "0 12px 12px 0",
                                        mb: 3,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: "'Poppins', sans-serif",
                                            fontWeight: 700,
                                            fontSize: { xs: "1.1rem", md: "1.4rem" },
                                            color: BRAND.white,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        We don't just{" "}
                                        <Box component="span" sx={{ color: BRAND.gold }}>teach</Box>{" "}
                                        software — we{" "}
                                        <Box component="span" sx={{ color: BRAND.green }}>build</Box>{" "}
                                        it too.
                                    </Typography>
                                </Box>

                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.95rem", color: BRAND.dimWhite, lineHeight: 1.75, mb: 3 }}>
                                    Our consultancy arm supports businesses at every stage — from idea to launch,
                                    from audit to scale. When you work with us, you get the same standard of
                                    excellence that drives our training programs.
                                </Typography>

                                {/* Service tags grid — 2 columns on sm+ */}
                                <Grid container spacing={1.5}>
                                    {CONSULT_SERVICES.map((s, i) => (
                                        <Grid item xs={12} sm={6} key={i}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.2,
                                                    p: "10px 16px",
                                                    backgroundColor: BRAND.cardBg,
                                                    border: `1px solid ${BRAND.border}`,
                                                    borderRadius: "8px",
                                                    transition: "border-color 0.3s, color 0.3s",
                                                    cursor: "default",
                                                    "&:hover": { borderColor: BRAND.gold, "& .svc-label": { color: BRAND.gold } },
                                                }}
                                            >
                                                <Box sx={{ color: BRAND.gold, display: "flex", fontSize: "1.1rem" }}>{s.icon}</Box>
                                                <Typography className="svc-label" sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", fontWeight: 500, color: BRAND.white, transition: "color 0.3s" }}>
                                                    {s.label}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Fade>
                        </Grid>

                        {/* Right column — CTA card */}
                        <Grid item xs={12} md={5}>
                            <Fade direction="right" triggerOnce delay={200}>
                                <Box
                                    sx={{
                                        p: 4,
                                        backgroundColor: BRAND.cardBg,
                                        border: `1px solid ${BRAND.border}`,
                                        borderRadius: "20px",
                                        textAlign: "center",
                                        backdropFilter: "blur(10px)",
                                        // Sticky top so it stays visible while left column scrolls on desktop
                                        position: { md: "sticky" },
                                        top: { md: "100px" },
                                    }}
                                >
                                    <BusinessCenterIcon sx={{ fontSize: 48, color: BRAND.gold, mb: 2 }} />
                                    <Typography
                                        sx={{
                                            fontFamily: "'Poppins', sans-serif",
                                            fontWeight: 800,
                                            fontSize: "1.45rem",
                                            color: BRAND.white,
                                            mb: 1.5,
                                            letterSpacing: "-0.01em",
                                        }}
                                    >
                                        Ready to Work With Us?
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.9rem", color: BRAND.dimWhite, lineHeight: 1.7, mb: 3 }}>
                                        Whether you're a startup or an enterprise — we bring the same passion,
                                        quality, and commitment that defines everything we do.
                                    </Typography>

                                    {/*
                   * GoldButton → navigate("/signup")
                   * react-router navigate() avoids a hard page reload.
                   * The user stays in the SPA — no white flash, no lost state.
                   */}
                                    <GoldButton fullWidth onClick={() => navigate("/signup")} startIcon={<RocketLaunchIcon />}>
                                        Get in Touch
                                    </GoldButton>
                                </Box>
                            </Fade>
                        </Grid>
                    </Grid>
                </Container>
            </SectionWrapper>

            {/* ════════════════════════════════════════════════════
          8. FOOTER STRIP
          Minimal single-line brand statement at page bottom.
      ════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    borderTop: `1px solid ${BRAND.border}`,
                    py: 2.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: { xs: 3, md: 6 },
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.8rem", color: BRAND.dimWhite }}>
                    <Box component="span" sx={{ color: BRAND.gold, fontWeight: 700 }}>Mission: </Box>
                    Train. Certify. Inspire. Consult. Deliver.
                </Typography>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.75rem", color: BRAND.dimWhite, letterSpacing: "0.06em" }}>
                    Gamified · Adaptive · Mastery-Based · SaaS
                </Typography>
            </Box>

        </PageWrapper>
    );
}
